import { z } from "zod";
import { ER_STAKEHOLDER_TYPES } from "./model";
import { isLegacyPracticalPhone, LEGACY_PHONE_INVALID_MESSAGE } from "./phoneLegacy";
import { requiresCommunicationsRoleJobTitle } from "./roleMap";

const YES_NO_VALUES = ["Yes", "No"] as const;

const expressCoreSchema = z.object({
  stakeholderType: z.string().trim().min(1, "Stakeholder type is required"),
  jobTitle: z.string().trim().optional().default(""),
  isUnaffiliatedProfessionalErTrainer: z.string().trim().optional().default(""),
  communicationsRole: z.string().trim().optional().default(""),
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  phone: z.string().trim().min(1, "Phone is required"),
  email: z.email("Valid email is required"),
  state: z.string().trim().min(1, "State is required"),
  county: z.string().trim().min(1, "County is required"),
  password: z.string().optional().default(""),
  passwordConfirm: z.string().optional().default(""),
});

function refineExpressCore(
  v: z.infer<typeof expressCoreSchema>,
  ctx: z.RefinementCtx,
  promptPassword: boolean,
): void {
  if (ER_STAKEHOLDER_TYPES.includes(v.stakeholderType as (typeof ER_STAKEHOLDER_TYPES)[number])) {
    if (!v.jobTitle.trim()) {
      ctx.addIssue({
        path: ["jobTitle"],
        code: "custom",
        message: "Job Title is required for Emergency Responder registrations",
      });
    }
    if (
      !YES_NO_VALUES.includes(
        v.isUnaffiliatedProfessionalErTrainer as (typeof YES_NO_VALUES)[number],
      )
    ) {
      ctx.addIssue({
        path: ["isUnaffiliatedProfessionalErTrainer"],
        code: "custom",
        message: "Please select Yes or No",
      });
    }
  }

  if (requiresCommunicationsRoleJobTitle(v.jobTitle) && !v.communicationsRole.trim()) {
    ctx.addIssue({
      path: ["communicationsRole"],
      code: "custom",
      message: "Communications Role is required for this job title",
    });
  }

  if (!isLegacyPracticalPhone(v.phone)) {
    ctx.addIssue({
      path: ["phone"],
      code: "custom",
      message: LEGACY_PHONE_INVALID_MESSAGE,
    });
  }

  if (promptPassword) {
    const password = v.password.trim();
    const passwordConfirm = v.passwordConfirm.trim();
    if (password.length < 8) {
      ctx.addIssue({
        path: ["password"],
        code: "custom",
        message: "Password must be at least 8 characters",
      });
    } else if (
      !/[A-Z]/.test(password) ||
      !/[^A-Za-z0-9]/.test(password) ||
      !/[0-9]/.test(password)
    ) {
      ctx.addIssue({
        path: ["password"],
        code: "custom",
        message:
          "Use at least 8 characters, including one uppercase letter, one number, and one special character (such as !, @, or #).",
      });
    }
    if (!passwordConfirm) {
      ctx.addIssue({
        path: ["passwordConfirm"],
        code: "custom",
        message: "Please confirm your password",
      });
    } else if (password !== passwordConfirm) {
      ctx.addIssue({
        path: ["passwordConfirm"],
        code: "custom",
        message: "Password does not match",
      });
    }
  }
}

export function registrationExpressSchema(promptPassword: boolean) {
  return expressCoreSchema.superRefine((v, ctx) => refineExpressCore(v, ctx, promptPassword));
}

export type RegistrationExpressValues = z.infer<typeof expressCoreSchema>;
